/**
 * **What happens when a session ends** — ADR-0024, adopting FiveOX ADR-0079
 * and ADR-0041.
 *
 * An access token is a 15-minute JWT that every service verifies *locally*
 * against JWKS. Nothing asks whether the session behind it still exists. So a
 * practitioner who signs out of another FiveOX app — which revokes the whole
 * login group, ours included — changes nothing this app can see: the stored
 * token still verifies, every request still succeeds, and a ProGST tab that was
 * closed at that moment reopens from `localStorage` and carries on. That is the
 * report the platform fixed this against, verbatim: *"logout from one app,
 * still logged in another"*.
 *
 * Three things have to be true, and this module is the one place that holds
 * all three:
 *
 * 1. **We ask.** `session.watch()` is the shell's watchdog (SESSION_WATCHDOG
 *    §3a): `GET /session/status` on start, on focus, on a bfcache restore and
 *    every 60s, coalesced. A 401/403 ends the session; a 5xx, a timeout or a
 *    thrown fetch does **not** — auth being unavailable must never sign a
 *    practitioner out, which is the worse bug of the two.
 * 2. **We listen, scoped.** The ~2s sign-out broadcast has existed estate-wide
 *    since 2026-08-14 and this app never registered for it. It does now — with
 *    its own user id, so that another app tidying up somebody *else's* dead
 *    session does not sign this person out (`signout.ts`, module note).
 * 3. **We forget the firm.** See `clearsTheOrgOnEnd` below.
 *
 * The watchdog itself is emphatically **not** copied here. `lib/session.ts` is
 * `createSessionStore()`, so `watch()` is already a method on the store we use;
 * the platform's own document files this repo under §3b — "your session module
 * is your own" — and that is a misfiling, recorded in ADR-0024 and owed back as
 * a finding. Copying 110 lines would fork the policy of what a dead session
 * means, which is exactly what drifts.
 */

import { onSignOut } from '@fiveox/shell'

import { setOrgId } from './api'
import { session } from './session'

/**
 * **The firm does not outlive the session that chose it** (ADR-0024, decision 4).
 *
 * The platform clears the shared parent-domain org cookie inside
 * `broadcastSignOut()`, which fixed this for the five core apps. **We are
 * outside that fix**, because we keep our own per-app key — deliberately, since
 * storage is per-origin and one app's selection must never be revived from
 * another's. So without this, a session ending on a shared browser leaves the
 * previous firm selected and the next person to sign in inherits it.
 *
 * Under ADR-0002 the org **is the CA firm**, so that residue is not a dropdown
 * remembering the wrong value: it is the wrong firm's name in the bar and the
 * wrong firm's client list one click away, for whoever sits down next. A shared
 * workstation in a practice is the normal case.
 *
 * **On the transition, not on the state.** Clearing whenever the session is
 * merely *absent* would also fire during the SSO round trip, when `/callback`
 * renders with no session for a moment before it stores the tokens — and would
 * quietly delete the remembered firm on every single sign-in, which
 * `resolveSelected` exists to honour. So this fires only when a session that
 * **existed** has gone.
 */
function clearsTheOrgOnEnd(): () => void {
  let had = session.getSession() !== null
  return session.subscribe(() => {
    const has = session.getSession() !== null
    if (had && !has) setOrgId(null)
    had = has
  })
}

/**
 * Arm all three. Mounted **once**, from the guard that already owns the
 * *no session → SSO* decision; the returned unsubscribe is what an effect's
 * cleanup wants.
 *
 * Nothing here navigates. The guard reads the session through
 * `useSyncExternalStore`, so ending it re-renders the app into `SsoRedirect` on
 * its own — and that is the authoritative path, because accounts, with a live
 * session, is what actually decides who this is.
 */
export function armSessionEnd(): () => void {
  const stopWatchdog = session.watch()
  const stopOrgClear = clearsTheOrgOnEnd()
  // `forUser` is read at the moment a broadcast arrives, never captured: a
  // refresh rotates the session and a captured id would be stale.
  const stopListener = onSignOut(
    () => session.clearSession(),
    () => session.getSession()?.userId ?? null,
  )
  return () => {
    stopWatchdog()
    stopOrgClear()
    stopListener()
  }
}
